import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { CheckCircle2, XCircle, Timer, Bot, AlertTriangle, Clock } from 'lucide-react'
import { LoadingSpinner } from '@/components/common/feedback/LoadingSpinner'
import { usePublishPipeline } from '../hooks/useAnalysis'

const STATUS_STYLE: Record<string, string> = {
  COMPLETED: 'bg-green-100 text-green-800 border-green-200',
  FAILED: 'bg-red-100 text-red-800 border-red-200',
  RUNNING: 'bg-blue-100 text-blue-800 border-blue-200',
  CANCELLED: 'bg-muted text-muted-foreground',
  PENDING: 'bg-amber-100 text-amber-800 border-amber-200',
}

function StatCard({
  title,
  value,
  subtitle,
  icon,
  tone,
}: {
  title: string
  value: string | number
  subtitle?: string
  icon: React.ReactNode
  tone?: 'danger' | 'warning' | 'good'
}) {
  const toneClass =
    tone === 'danger'
      ? 'border-red-200 bg-red-50/50'
      : tone === 'warning'
        ? 'border-amber-200 bg-amber-50/40'
        : tone === 'good'
          ? 'border-green-200 bg-green-50/40'
          : ''
  return (
    <Card className={toneClass}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="text-sm font-medium font-sans text-muted-foreground">
          {title}
        </CardTitle>
        <div className="text-muted-foreground/70">{icon}</div>
      </CardHeader>
      <CardContent className="space-y-1">
        <div className="text-4xl font-serif font-semibold tracking-tight">{value}</div>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </CardContent>
    </Card>
  )
}

function duration(seconds: number | null) {
  if (seconds === null || Number.isNaN(seconds)) return '—'
  if (seconds < 90) return `${seconds}s`
  return `${Math.round(seconds / 60)}m`
}

export function PublishedDashboard() {
  const { data, isLoading, error } = usePublishPipeline()

  if (isLoading) return <LoadingSpinner />
  if (error || !data) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>Could not load publishing pipeline stats.</AlertDescription>
      </Alert>
    )
  }

  const { byStatus, autoSweep, failureReasons, recentFailures, timing } = data
  const completed = byStatus.find((s) => s.status === 'COMPLETED')?.count ?? 0
  const failed = byStatus.find((s) => s.status === 'FAILED')?.count ?? 0
  const attempted = completed + failed
  const successRate = attempted ? Math.round((completed / attempted) * 100) : null

  return (
    <div className="space-y-6">
      {data.stuckCount > 0 && (
        <Alert variant="destructive">
          <Clock className="h-4 w-4" />
          <AlertDescription>
            <span className="font-medium">{data.stuckCount}</span> publish task
            {data.stuckCount === 1 ? '' : 's'} started over an hour ago and never finished. These
            leave an artwork marked published here while it may not be live on Shopify.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Published"
          value={data.publishedTotal.toLocaleString()}
          subtitle="artworks live"
          icon={<CheckCircle2 className="h-5 w-5" />}
        />
        <StatCard
          title="Publish Success"
          value={successRate === null ? '—' : `${successRate}%`}
          subtitle={`${completed} succeeded of ${attempted} attempted`}
          icon={<CheckCircle2 className="h-5 w-5" />}
          tone={successRate !== null && successRate < 80 ? 'warning' : 'good'}
        />
        <StatCard
          title="Failed Tasks"
          value={failed}
          subtitle="see reasons below"
          icon={<XCircle className="h-5 w-5" />}
          tone={failed > 0 ? 'danger' : undefined}
        />
        <StatCard
          title="Median Publish Time"
          value={duration(timing.median_seconds)}
          subtitle={`across ${timing.sampled} completed`}
          icon={<Timer className="h-5 w-5" />}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-xl">Why Publishes Fail</CardTitle>
            <p className="text-sm text-muted-foreground">
              Grouped by cause — artwork ids normalised to N
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {failureReasons.length === 0 ? (
              <p className="text-sm text-muted-foreground">No failures recorded.</p>
            ) : (
              failureReasons.map((f) => (
                <div key={f.reason} className="space-y-1 border-b pb-3 last:border-0 last:pb-0">
                  <div className="flex items-start gap-3">
                    <span className="text-sm leading-snug">{f.reason}</span>
                    <span className="ml-auto shrink-0 font-medium tabular-nums">{f.count}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    last seen {new Date(f.last_seen).toLocaleString()}
                  </p>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-xl">Auto-Publish Sweep</CardTitle>
            <p className="text-sm text-muted-foreground">
              {data.autoPublishEnabled
                ? 'Running every 15 minutes'
                : 'Currently off — these are from manual runs'}
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="text-2xl font-serif font-semibold">{autoSweep.total}</div>
                <p className="text-xs text-muted-foreground">Attempted</p>
              </div>
              <div>
                <div className="text-2xl font-serif font-semibold text-green-700">
                  {autoSweep.completed}
                </div>
                <p className="text-xs text-muted-foreground">Published</p>
              </div>
              <div>
                <div
                  className={`text-2xl font-serif font-semibold ${autoSweep.failed > 0 ? 'text-red-700' : ''}`}
                >
                  {autoSweep.failed}
                </div>
                <p className="text-xs text-muted-foreground">Failed</p>
              </div>
            </div>
            <div className="flex items-center gap-2 border-t pt-3 text-sm text-muted-foreground">
              <Bot className="h-4 w-4" />
              <span>Last sweep publish</span>
              <span className="ml-auto font-medium text-foreground">
                {autoSweep.last_run_at ? new Date(autoSweep.last_run_at).toLocaleString() : 'Never'}
              </span>
            </div>
            <div className="space-y-2 border-t pt-3">
              <p className="text-xs font-medium text-muted-foreground">All publish tasks</p>
              <div className="flex flex-wrap gap-2">
                {byStatus.map((s) => (
                  <Badge
                    key={s.status}
                    variant="outline"
                    className={STATUS_STYLE[s.status] || ''}
                  >
                    {s.status.toLowerCase()} · {s.count}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {recentFailures.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-xl">Recent Failures</CardTitle>
            <p className="text-sm text-muted-foreground">Most recent first</p>
          </CardHeader>
          <CardContent className="space-y-3">
            {recentFailures.map((f, i) => (
              <div
                key={`${f.artwork_id}-${i}`}
                className="space-y-1 border-b pb-3 last:border-0 last:pb-0"
              >
                <div className="flex items-baseline gap-2">
                  <span className="font-medium">{f.title || `Artwork ${f.artwork_id}`}</span>
                  <span className="text-xs text-muted-foreground">#{f.artwork_id}</span>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {new Date(f.created_at).toLocaleString()}
                  </span>
                </div>
                <p className="text-sm text-red-800">{f.error_message}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
